/**
 * Input.
 *
 * Keyboard, swipe, on-screen pad and gamepad all funnel into one `onTurn`.
 * The original game dropped inputs that arrived between ticks; here every turn
 * is handed to the simulation's two-deep buffer, which is the single biggest
 * reason this version feels better to play than the 2022 one.
 */

import type { Dir } from '../core/types'

type TurnHandler = (dir: Dir) => void
type ActionHandler = (action: Action) => void

export type Action = 'pause' | 'restart' | 'back' | 'confirm' | 'mute'

const KEY_DIRS: Record<string, Dir> = {
  ArrowUp: 0,
  KeyW: 0,
  ArrowRight: 1,
  KeyD: 1,
  ArrowDown: 2,
  KeyS: 2,
  ArrowLeft: 3,
  KeyA: 3,
}

const KEY_ACTIONS: Record<string, Action> = {
  Space: 'pause',
  KeyP: 'pause',
  Escape: 'back',
  KeyR: 'restart',
  Enter: 'confirm',
  KeyM: 'mute',
}

export class Input {
  onTurn: TurnHandler = () => {}
  onAction: ActionHandler = () => {}
  /** Any input at all — used to unlock audio and dismiss the attract screen. */
  onAny: () => void = () => {}

  private padIndex: number | null = null
  private padAxisLatch = false
  private detach: Array<() => void> = []

  attach(surface: HTMLElement): void {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return

      const dir = KEY_DIRS[e.code]
      if (dir !== undefined) {
        e.preventDefault()
        this.onAny()
        this.onTurn(dir)
        return
      }
      const action = KEY_ACTIONS[e.code]
      if (action) {
        e.preventDefault()
        this.onAny()
        this.onAction(action)
      }
    }
    window.addEventListener('keydown', onKey, { passive: false })
    this.detach.push(() => window.removeEventListener('keydown', onKey))

    // ── swipe ────────────────────────────────────────────────────────────
    let sx = 0
    let sy = 0
    let tracking = false
    const THRESHOLD = 22

    const start = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return
      tracking = true
      sx = e.clientX
      sy = e.clientY
      this.onAny()
    }
    const move = (e: PointerEvent) => {
      if (!tracking) return
      const dx = e.clientX - sx
      const dy = e.clientY - sy
      if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return
      // Re-arm from the current point so a single long drag can trace a whole
      // path of turns — surprisingly good on a phone.
      sx = e.clientX
      sy = e.clientY
      this.onTurn(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 2 : 0)
    }
    const end = () => {
      tracking = false
    }

    surface.addEventListener('pointerdown', start, { passive: true })
    surface.addEventListener('pointermove', move, { passive: true })
    surface.addEventListener('pointerup', end, { passive: true })
    surface.addEventListener('pointercancel', end, { passive: true })
    this.detach.push(() => {
      surface.removeEventListener('pointerdown', start)
      surface.removeEventListener('pointermove', move)
      surface.removeEventListener('pointerup', end)
      surface.removeEventListener('pointercancel', end)
    })

    const onGamepad = (e: GamepadEvent) => {
      this.padIndex = e.gamepad.index
    }
    window.addEventListener('gamepadconnected', onGamepad)
    this.detach.push(() => window.removeEventListener('gamepadconnected', onGamepad))
  }

  /** Bind an on-screen button. Pointerdown, not click — latency matters. */
  bindButton(el: HTMLElement, dir: Dir): void {
    const handler = (e: Event) => {
      e.preventDefault()
      this.onAny()
      this.onTurn(dir)
    }
    el.addEventListener('pointerdown', handler)
    this.detach.push(() => el.removeEventListener('pointerdown', handler))
  }

  /** Poll pads once per frame; they have no event model for axes. */
  pollGamepad(): void {
    if (this.padIndex === null || !navigator.getGamepads) return
    const pad = navigator.getGamepads()[this.padIndex]
    if (!pad) return

    const [ax, ay] = [pad.axes[0] ?? 0, pad.axes[1] ?? 0]
    const dpad: Array<[number, Dir]> = [
      [12, 0],
      [15, 1],
      [13, 2],
      [14, 3],
    ]
    for (const [button, dir] of dpad) {
      if (pad.buttons[button]?.pressed) {
        this.onAny()
        this.onTurn(dir)
        return
      }
    }
    if (Math.abs(ax) > 0.55 || Math.abs(ay) > 0.55) {
      if (!this.padAxisLatch) {
        this.padAxisLatch = true
        this.onAny()
        this.onTurn(Math.abs(ax) > Math.abs(ay) ? (ax > 0 ? 1 : 3) : ay > 0 ? 2 : 0)
      }
    } else {
      this.padAxisLatch = false
    }
    if (pad.buttons[9]?.pressed) this.onAction('pause')
    if (pad.buttons[0]?.pressed) this.onAction('confirm')
  }

  dispose(): void {
    for (const fn of this.detach) fn()
    this.detach = []
  }
}
